// Keeping a deck's pictures honest: the deck on screen is the only source of
// truth for what a saved file and an export may contain.
//
// Every picture in a deck lives inline — a block's `src`, a background's value,
// a title card's `image` — as a base64 data URL when it was uploaded. There is
// no asset registry to leak, but two habits let dead bytes accumulate:
//
//   1. The content modal saves `{...formData, ...blockToSave}`. A gallery
//      edited down to a single picture keeps its old `items` array — with every
//      base64 source in it — on a block that no longer renders items.
//   2. A block switched from an upload to a hosted link keeps the still picture
//      captured for the exports, so the base64 outlives the file it replaced.
//
// `cleanDeckImages` removes those, and only those: data that is provably
// unreachable from the block it hangs off. It does not touch `slideBackgrounds`
// — see the note there for the two rules that once did and the backgrounds they
// destroyed.
//
// `dedupeDeckImages` and `expandDeckImages` are the serialization pair:
// identical pictures are written once into an `imageAssets` table on the way
// out and put back on the way in, so nothing in the app's render or export
// layers has to learn about references — in memory a picture is always the
// picture. That is what keeps one background used by thirty-one slides to one
// copy in the file, without editing what any slide points at.

import { isDurableExternalUrl } from './assetSources.js';

const ASSET_REF_PREFIX = 'asset:';

// A duplicate has to be worth a reference. Interning a 60-byte tracking pixel
// costs more in indirection than it saves in bytes.
const MIN_DEDUPE_BYTES = 1024;

export const isDataUrlString = (value) => typeof value === 'string' && /^data:image\//i.test(value);

const isAssetRef = (value) => typeof value === 'string' && value.startsWith(ASSET_REF_PREFIX);

const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : null);

// Fields the content modal keeps for itself. They ride onto a block through the
// `{...formData, ...blockToSave}` spread and mean nothing once it is saved.
const TRANSIENT_BLOCK_FIELDS = ['imageFiles', 'imageEmbedInput', 'isEditing', 'pastedImageUpdates'];

// `items` is how a gallery, an accordion, a timeline and stacked cards each
// hold their entries. A single image block has no use for one, so an `items`
// array on a block of type 'image' is what a gallery left behind.
const TYPES_WITHOUT_ITEMS = new Set(['image']);

const cleanBlock = (block, stats) => {
  if (!asObject(block)) return block;
  let next = block;
  const drop = (field, countAs) => {
    if (next[field] === undefined) return;
    const { [field]: removed, ...rest } = next;
    next = rest;
    if (isDataUrlString(removed)) stats[countAs] += 1;
    else if (Array.isArray(removed)) {
      stats[countAs] += removed.filter((item) => isDataUrlString(item?.src)).length;
    }
  };

  TRANSIENT_BLOCK_FIELDS.forEach((field) => drop(field, 'transientFields'));

  if (TYPES_WITHOUT_ITEMS.has(block.type) && Array.isArray(block.items)) {
    drop('items', 'orphanedGalleryImages');
  }

  // An upload that a hosted link replaced. Both fields describing one picture
  // is the shape a deck ends up in when the link was added after the upload;
  // the link is the source of truth, so the bytes beside it are the leftover.
  // Metadata — alt, caption, citation, size, frame — is untouched.
  if (isDataUrlString(next.src) && isDurableExternalUrl(next.imageEmbedSrc)) {
    drop('src', 'supersededUploads');
  }

  // The still picture exists for PowerPoint and PDF, which cannot host a
  // provider's iframe. A block with no embed has nothing to stand in for, and a
  // block whose embed is the picture itself already has the picture — keeping
  // the base64 beside a working URL is exactly the duplicate to shed.
  if (isDataUrlString(next.imageEmbedThumbnail)) {
    const embedSrc = typeof next.imageEmbedSrc === 'string' ? next.imageEmbedSrc : '';
    const isDirectPicture = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|#|$)/i.test(embedSrc);
    if (!embedSrc || isDirectPicture) drop('imageEmbedThumbnail', 'supersededStills');
  }

  return next;
};

const cleanSections = (sections, stats) => {
  const mapBlocks = (section) => {
    if (!asObject(section) || !Array.isArray(section.blocks)) return section;
    return { ...section, blocks: section.blocks.map((block) => cleanBlock(block, stats)) };
  };

  if (Array.isArray(sections)) return sections.map(mapBlocks);
  const object = asObject(sections);
  if (!object) return sections;
  return Object.entries(object).reduce((acc, [key, section]) => {
    acc[key] = mapBlocks(section);
    return acc;
  }, {});
};

/**
 * Drop the picture data a block can no longer reach.
 *
 * Nothing that renders is touched: every background, every block's own `src`
 * and every title card's picture survive exactly as they were.
 *
 * @returns {{deck, stats}} stats counts what went, for the save/export report.
 */
export const cleanDeckImages = (deck) => {
  const stats = {
    orphanedGalleryImages: 0,
    supersededStills: 0,
    supersededUploads: 0,
    transientFields: 0
  };
  if (!asObject(deck)) return { deck, stats };

  // `slideBackgrounds` is deliberately untouched.
  //
  // Two rules used to edit it and both destroyed real backgrounds. Dropping an
  // entry whose slide id could not be found assumed the ids were all readable —
  // they are stamped onto blocks in state, after a file loads, so a deck
  // regularly shows only some of them and every unseen slide looked deleted.
  // Dropping an entry that merely equalled the deck background assumed
  // inheritance was the same thing as a value — it renders the same today and
  // then follows the deck the next time the deck background changes, which is
  // not what the author chose.
  //
  // Neither was worth having: identical pictures already cost one copy through
  // the asset table below, and deleting a slide already forgets its background
  // at the moment of deletion, which is the one point where the question can be
  // answered for certain. What is left here only removes data that is provably
  // unreachable — a gallery's leftovers on a block that renders none, a still
  // superseded by the picture itself, an upload superseded by a hosted address.
  return { deck: { ...deck, sections: cleanSections(deck.sections, stats) }, stats };
};

// --- serialization: one copy of a picture, however many places use it -------

const walkStrings = (value, visit) => {
  if (typeof value === 'string') return visit(value);
  if (Array.isArray(value)) return value.map((entry) => walkStrings(entry, visit));
  const object = asObject(value);
  if (!object) return value;
  return Object.entries(object).reduce((acc, [key, entry]) => {
    acc[key] = walkStrings(entry, visit);
    return acc;
  }, {});
};

/**
 * Write each repeated picture once.
 *
 * A picture used by three slides is three identical base64 strings in the file.
 * Here the second and later uses become `asset:N` and the bytes are written
 * once into `imageAssets`. `expandDeckImages` puts them back on load, so the
 * in-memory deck never contains a reference and no renderer or exporter needs
 * to resolve one.
 *
 * A file that gains an `imageAssets` table needs a build that expands it —
 * older builds would show its shared pictures as broken.
 */
export const dedupeDeckImages = (deck) => {
  if (!asObject(deck)) return deck;

  const counts = new Map();
  walkStrings(deck, (value) => {
    if (isDataUrlString(value) && value.length >= MIN_DEDUPE_BYTES) {
      counts.set(value, (counts.get(value) || 0) + 1);
    }
    return value;
  });

  const refs = new Map();
  const imageAssets = {};
  let nextId = 1;
  counts.forEach((count, value) => {
    if (count < 2) return;
    const ref = `${ASSET_REF_PREFIX}${nextId}`;
    nextId += 1;
    refs.set(value, ref);
    imageAssets[ref] = value;
  });

  if (!refs.size) return deck;

  const rewritten = walkStrings(deck, (value) => refs.get(value) || value);
  return { ...rewritten, imageAssets };
};

/**
 * Put the shared pictures back. A file without an `imageAssets` table — every
 * file written before this existed — passes through untouched.
 */
export const expandDeckImages = (deck) => {
  const table = asObject(deck?.imageAssets);
  if (!table) return deck;

  const expanded = walkStrings(deck, (value) => (
    isAssetRef(value) && typeof table[value] === 'string' ? table[value] : value
  ));
  const { imageAssets, ...rest } = expanded;
  return rest;
};

/** Everything a save does to a deck's pictures, in the order it must happen. */
export const prepareDeckForSave = (deck) => {
  const { deck: cleaned, stats } = cleanDeckImages(deck);
  return { deck: dedupeDeckImages(cleaned), stats };
};

/** Everything a load does: resolve shared pictures, then clean what arrived. */
export const prepareLoadedDeck = (deck) => cleanDeckImages(expandDeckImages(deck)).deck;
